// /opt/isp_erp/frontend/src/components/DateTimeSelector.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Typography
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { scheduleAPI } from '../api/schedule.api';

const DateTimeSelector = ({ technicianId, onDateTimeChange, durationMinutes = 90 }) => {
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAvailable, setIsAvailable] = useState(null);

  // Cargar horarios disponibles cuando cambia el técnico o la fecha
  useEffect(() => {
    if (technicianId && selectedDateTime) {
      checkAvailability();
    }
  }, [technicianId, selectedDateTime]);

  const checkAvailability = async () => {
    if (!technicianId || !selectedDateTime) return;
    
    setLoading(true);
    setError('');
    setIsAvailable(null);
    
    try {
      const result = await scheduleAPI.checkAvailability(
        technicianId,
        selectedDateTime,
        durationMinutes
      );
      setIsAvailable(result.available);
      
      if (!result.available) {
        setError('El técnico no está disponible en este horario. Por favor selecciona otro horario.');
        // Cargar horarios disponibles alternativos
        loadAvailableSlots();
      } else {
        onDateTimeChange(selectedDateTime);
      }
    } catch (err) {
      setError('Error al verificar disponibilidad');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async () => {
    if (!technicianId || !selectedDateTime) return;
    
    try {
      const result = await scheduleAPI.getAvailableSlots(
        technicianId,
        selectedDateTime,
        durationMinutes
      );
      setAvailableSlots(result.available_slots || []);
    } catch (err) {
      console.error('Error loading available slots:', err);
    }
  };

  const handleDateTimeChange = (newValue) => {
    setSelectedDateTime(newValue);
    setIsAvailable(null);
    setAvailableSlots([]);
    setError('');
  };

  const selectAvailableSlot = (slot) => {
    setSelectedDateTime(new Date(slot.start_time));
    onDateTimeChange(new Date(slot.start_time));
    setAvailableSlots([]);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box sx={{ width: '100%' }}>
        <DateTimePicker
          label="Fecha y hora programada"
          value={selectedDateTime}
          onChange={handleDateTimeChange}
          disabled={!technicianId}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
              helperText: !technicianId ? 'Primero selecciona un técnico' : ''
            }
          }}
        />
        
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="caption">Verificando disponibilidad...</Typography>
          </Box>
        )}
        
        {isAvailable === true && (
          <Alert severity="success" sx={{ mt: 1 }}>
            ✅ Técnico disponible en este horario
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        
        {availableSlots.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Horarios disponibles sugeridos:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availableSlots.slice(0, 5).map((slot, idx) => (
                <Chip
                  key={idx}
                  label={new Date(slot.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  onClick={() => selectAvailableSlot(slot)}
                  color="primary"
                  variant="outlined"
                  clickable
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DateTimeSelector;
